import { type HeatingNetwork } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { Alert } from "@eisox/design-system";
import { ArrowRightIcon, CrossIcon } from "@eisox/icons";

import { applySlot, type RoomFrostFreeSlots } from "~/features/frost-free/helpers/actions.helper";
import {
  FrostFreeSource,
  type FrostFreePlannedRoomResult,
  type FrostFreeRoomResult,
  type RoomFrostFreeSource,
} from "~/features/frost-free/helpers/status.helper";
import { m } from "~/paraglide/messages";

const LABEL_BY_SOURCE: Record<RoomFrostFreeSource, () => string> = {
  [FrostFreeSource.BUILDING]: m.frost_free_activated,
  [FrostFreeSource.HEATING_NETWORK]: m.frost_free_activated_by_network,
  [FrostFreeSource.HEATING_NETWORK_PARTIAL]: m.frost_free_activated_by_network_partial,
  [FrostFreeSource.ROOM]: m.frost_free_activated,
};

interface ActiveProps {
  result: FrostFreeRoomResult;
  slots: RoomFrostFreeSlots;
  heatingNetworks: HeatingNetwork[];
  className?: string;
  onClose?: () => void;
}

const Active: React.FC<ActiveProps> = ({ result, slots, heatingNetworks, className, onClose }) => {
  if (!result.active) return null;

  const byNetwork =
    result.source === FrostFreeSource.HEATING_NETWORK || result.source === FrostFreeSource.HEATING_NETWORK_PARTIAL;

  const alert = (
    <Alert className={className}>
      <div className="gap-1 flex flex-col items-center">
        {LABEL_BY_SOURCE[result.source]()}
        {result.period && (
          <span>
            {dayjs(result.period.begin).format("L")} - {dayjs(result.period.end).format("L")}
          </span>
        )}
        {byNetwork && result.heatingNetworks && (
          <ul className="gap-1 flex flex-col">
            {Object.entries(result.heatingNetworks).map(([heatingNetworkId, networkResult]) => {
              const heatingNetwork = heatingNetworks.find(hn => hn.id === heatingNetworkId);
              if (!networkResult.active || !heatingNetwork) return null;
              return (
                <li key={heatingNetworkId} className="list-inside list-disc text-sm">
                  {heatingNetwork.name}
                  {networkResult.period &&
                    ` - ${dayjs(networkResult.period.begin).format("L")} - ${dayjs(networkResult.period.end).format("L")}`}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {!byNetwork && <ArrowRightIcon />}
      {result.source === FrostFreeSource.HEATING_NETWORK_PARTIAL && onClose && (
        <CrossIcon className="cursor-pointer" onClick={onClose} />
      )}
    </Alert>
  );

  return <>{applySlot(result.source, alert, slots)}</>;
};

Active.displayName = "RoomFrostFreeAlert.Active";

interface PlannedProps {
  result: FrostFreePlannedRoomResult;
  slots: RoomFrostFreeSlots;
  className?: string;
}

const Planned: React.FC<PlannedProps> = ({ result, slots, className }) => {
  if (!result.planned) return null;

  const alert = (
    <Alert className={className}>
      {m.frost_free_planned()}
      <ArrowRightIcon />
    </Alert>
  );

  return <>{applySlot(result.source, alert, slots)}</>;
};

Planned.displayName = "RoomFrostFreeAlert.Planned";

export const RoomFrostFreeAlert = { Active, Planned };
